<template>
  <section class="workout-overview" aria-label="Trainingsübersicht">
    <div class="overview-progress">
      <div class="progress-ring" role="img" :aria-label="`${Math.round(progress)} Prozent abgeschlossen`">
        <svg viewBox="0 0 120 120" aria-hidden="true">
          <circle class="ring-track" cx="60" cy="60" r="50" />
          <circle v-if="progress > 0" class="ring-value" cx="60" cy="60" r="50" pathLength="100" :stroke-dasharray="`${progress} 100`" />
        </svg>
        <strong>{{ Math.round(progress) }}<small>%</small></strong>
      </div>
      <div class="progress-copy">
        <span class="overview-eyebrow">Dein Training</span>
        <h2>{{ total && completed === total ? 'Geschafft.' : 'Satz für Satz.' }}</h2>
        <p>{{ completed }} von {{ total }} Übungen erledigt</p>
      </div>
    </div>

    <dl class="overview-metrics">
      <div><dt>Dauer</dt><dd>{{ minutes }} <small>Min</small></dd></div>
      <div><dt>Volumen</dt><dd>{{ formattedWeight }} <small>kg</small></dd></div>
      <div><dt>Kalorien <span>· geschätzt</span></dt><dd>{{ calories }} <small>kcal</small></dd></div>
    </dl>

    <button v-if="nextExercise" type="button" class="overview-next" @click="$emit('next')">
      <img :src="nextImage" alt="" loading="lazy" />
      <span class="next-copy">
        <span class="overview-eyebrow">Als Nächstes</span>
        <strong>{{ nextExercise.title }}</strong>
        <small>{{ nextSetCount ? `${nextSetCount} Sätze geplant` : 'Bereit für deinen nächsten Satz' }}</small>
      </span>
      <v-icon color="white" size="22">mdi-arrow-right</v-icon>
    </button>
    <p v-else class="overview-complete">
      <v-icon small :color="accent">{{ total ? 'mdi-check-circle-outline' : 'mdi-dumbbell' }}</v-icon>
      {{ total ? 'Alle Übungen erledigt. Du kannst dein Training unten abschliessen.' : 'Füge oben deine erste Übung hinzu.' }}
    </p>

    <div v-if="focusItems.length" class="overview-focus">
      <div class="focus-heading"><h3>Trainingsfokus</h3><span>Dein Plan</span></div>
      <div v-for="item in focusItems" :key="item.name" class="focus-row">
        <span>{{ item.name }}</span>
        <div class="focus-track" aria-hidden="true"><div :style="{ width: `${item.percent}%` }"></div></div>
        <small>{{ item.count }} <span class="sr-only">Muskelzuordnungen</span></small>
      </div>
    </div>
  </section>
</template>

<script>
export default {
  name: "RunWorkoutOverview",
  props: {
    progress: { type: Number, default: 0 },
    completed: { type: Number, default: 0 },
    total: { type: Number, default: 0 },
    minutes: { type: Number, default: 0 },
    weight: { type: Number, default: 0 },
    calories: { type: Number, default: 0 },
    nextExercise: { type: Object, default: null },
    nextImage: { type: String, default: "" },
    nextSetCount: { type: Number, default: 0 },
    focus: { type: Object, default: () => ({}) },
    accent: { type: String, default: "#8cdbb1" },
  },
  computed: {
    formattedWeight() {
      return this.weight.toLocaleString("de-CH")
    },
    focusItems() {
      const entries = Object.entries(this.focus).filter(([, count]) => Number.isFinite(count) && count > 0)
      const max = Math.max(1, ...entries.map(([, count]) => count))
      return entries.map(([name, count]) => ({ name, count, percent: count / max * 100 }))
    },
  },
}
</script>

<style scoped>
.workout-overview { padding: 20px; color: #f2f6f4; }
.overview-progress { display: flex; align-items: center; gap: 20px; margin-bottom: 22px; }
.progress-ring { position: relative; width: 108px; height: 108px; flex: 0 0 108px; }
.progress-ring svg { display: block; width: 100%; height: 100%; transform: rotate(-90deg); }
.progress-ring circle { fill: none; stroke-width: 7; }
.ring-track { stroke: rgba(255, 255, 255, .08); }
.ring-value { stroke: var(--overview-accent); stroke-linecap: round; }
.progress-ring strong { position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; font-size: 27px; font-weight: 700; font-variant-numeric: tabular-nums; }
.progress-ring small { margin-left: 2px; font-size: 13px; color: #9daea6; }
.progress-copy { min-width: 0; }
.overview-eyebrow { display: block; font-size: 10px; line-height: 1.5; font-weight: 700; letter-spacing: .1em; text-transform: uppercase; color: var(--overview-accent); }
.progress-copy h2 { margin: 4px 0 6px; font-size: 25px; line-height: 1.15; letter-spacing: -.04em; }
.progress-copy p { margin: 0; font-size: 12px; color: #aab9b1; }
.overview-metrics { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 8px; margin: 0 0 20px; }
.overview-metrics > div { padding: 12px 10px; border: 1px solid rgba(255,255,255,.08); border-radius: 14px; background: #1b2521; }
.overview-metrics dt { min-height: 30px; font-size: 11px; color: #aab9b1; }
.overview-metrics dt span { display: block; font-size: 9px; }
.overview-metrics dd { margin: 4px 0 0; font-size: clamp(17px, 5vw, 23px); font-weight: 650; line-height: 1.2; overflow-wrap: anywhere; font-variant-numeric: tabular-nums; }
.overview-metrics dd small { font-size: 10px; font-weight: 400; color: #aab9b1; white-space: nowrap; }
.overview-next { display: flex; width: 100%; align-items: center; gap: 12px; padding: 12px; border: 1px solid rgba(255,255,255,.12); border-radius: 16px; background: #202d27; color: inherit; text-align: left; }
.overview-next img { width: 62px; height: 68px; flex: 0 0 62px; object-fit: cover; border-radius: 10px; background: #eef1ef; }
.next-copy { display: grid; gap: 4px; flex: 1; min-width: 0; }
.next-copy strong { font-size: 16px; line-height: 1.25; overflow-wrap: anywhere; }
.next-copy small { color: #aab9b1; font-size: 11px; }
.overview-next:focus-visible { outline: 2px solid var(--overview-accent); outline-offset: 3px; }
.overview-complete { display: flex; gap: 10px; align-items: center; margin: 0; padding: 14px; border-radius: 14px; background: #1b2521; color: #b7c6bd; font-size: 12px; }
.overview-focus { margin-top: 24px; padding-bottom: 4px; }
.focus-heading { display: flex; align-items: center; justify-content: space-between; margin-bottom: 14px; }
.focus-heading h3 { font-size: 13px; font-weight: 600; }
.focus-heading > span { font-size: 10px; color: #aab9b1; }
.focus-row { display: grid; grid-template-columns: 54px minmax(0, 1fr) 22px; gap: 12px; align-items: center; margin-top: 12px; font-size: 12px; }
.focus-row small { text-align: right; color: #aab9b1; font-variant-numeric: tabular-nums; }
.focus-track { height: 6px; border-radius: 4px; background: rgba(255,255,255,.08); overflow: hidden; }
.focus-track > div { height: 100%; border-radius: inherit; background: var(--overview-accent); }
.sr-only { position: absolute; width: 1px; height: 1px; overflow: hidden; clip-path: inset(50%); }
@media (max-width: 350px) {
  .workout-overview { padding: 16px; }
  .overview-progress { gap: 14px; }
  .progress-ring { width: 88px; height: 88px; flex-basis: 88px; }
  .progress-copy h2 { font-size: 22px; }
  .overview-metrics > div { padding: 10px 8px; }
}
</style>
